<script>
import { REPORT_PATROL_TYPE_LIST } from '../utils/constants'
import { EVENT_CHART_TYPES } from '../utils/events'
import Endpoints from './Endpoints.vue'
import Request from './Request.vue'
import Common from './Common.vue'
import { applySessionScope, getSessionAccount, getSessionAccountId, getSessionClientId, getSessionGroupAccountIds, hasGlobalScope, scopeAccountId, scopeClientId } from '../utils/scope'
import { normalizeTimelinePage } from '../utils/timeline'
import moment from 'moment-timezone'

const buildClientScope = state => {
    if (hasGlobalScope(state)) return { status: 'ACTIVE', type: 'CLIENT' }

    const account = getSessionAccountId(state)
    if (!account) return null

    return { account, status: 'ACTIVE', type: 'CLIENT' }
}

const getTimelinePage = async (state, filters, endpoint) => {
    const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, endpoint)
    if (response instanceof Error || response?.isAxiosError) throw response
    return normalizeTimelinePage(response, filters)
}

// Usuário preso a um cliente enxerga só ele, mas vindo da API para manter os campos da listagem.
const keepOwnClient = (state, results) => {
    if (hasGlobalScope(state)) return results

    const client = getSessionClientId(state)
    if (!client) return results

    return results.filter(item => item?._id === client)
}

const buildSiteScope = state => {
    const client = getSessionClientId(state)
    if (client) return { client, status: 'ACTIVE', type: 'SITE' }

    if (hasGlobalScope(state)) return { status: 'ACTIVE', type: 'SITE' }

    const account = getSessionAccountId(state)
    if (!account) return null

    return { account, status: 'ACTIVE', type: 'SITE' }
}

const extractResponseItems = response => {
    if (!response) return []

    const data = response.data

    if (Array.isArray(data)) return data
    if (Array.isArray(data?.results)) return data.results
    if (Array.isArray(data?.result)) return data.result

    return []
}

export default {
    getMe: async function (state) {
        const response = await Request.do(state, 'GET', Request.getDefaultHeader(state), {}, `${Endpoints.systemUsers.getMe}`)
        return response?.data?.result || {}
    },
    getLegacyId: async function (state) {
        const me = await this.getMe(state)
        return me?.account?.companyLegacyId || null
    },
    getAccounts: async function (state, { page = 1, pageSize = 100 } = {}) {
        if (!hasGlobalScope(state) && getSessionGroupAccountIds(state).length === 0) {
            const account = getSessionAccount(state)
            return account ? [account] : []
        }

        const filters = {
            name: '',
            status: 'ACTIVE',
            type: 'ACCOUNT',
            page,
            pageSize,
        }
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.companies.filter}`)
        return response?.data?.results || []
    },
    getCompanySettings: async function (state, filters = {}) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.companySettings.filter}`)
        return response?.data?.result || {}
    },
    getMyCompanySettings: async function (state) {
        const response = await Request.do(state, 'GET', Request.getDefaultHeader(state), {}, `${Endpoints.companySettings.me}`)
        return response?.data?.result || {}
    },
    getCompanySettingsEventTypes: async function (state) {
        const response = await Request.do(state, 'GET', Request.getDefaultHeader(state), {}, `${Endpoints.helpers.monitorEventTypes}`)
        return (response?.data?.results || []).map(item => ({
            type: item?._id || item?.type || '',
            label: item?.name || item?.label || '',
        })).filter(item => item.type && item.label)
    },
    updateCompanySettings: async function (state, payload) {
        const response = await Request.do(state, 'PUT', Request.getDefaultHeader(state), payload, `${Endpoints.companySettings.companySetting}`)
        return response?.data?.result || {}
    },
    filterAccounts: async function (state) {
        if (!hasGlobalScope(state) && getSessionGroupAccountIds(state).length === 0) {
            const account = getSessionAccount(state)
            return account ? [account] : []
        }

        const filters = {
            status: 'ACTIVE',
            type: 'ACCOUNT',
        }
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.companies.filterV2}`)
        return response?.data?.results || []
    },
    filterClients: async function (state) {
        const filters = buildClientScope(state)
        if (!filters) return []

        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.companies.filterV2}`)
        return keepOwnClient(state, response?.data?.results || [])
    },
    getClients: async function (state, { page = 1, pageSize = 100 } = {}) {
        const scope = buildClientScope(state)
        if (!scope) return []

        const filters = {
            name: '',
            ...scope,
            page,
            pageSize,
        }

        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.companies.filter}`)
        return keepOwnClient(state, response?.data?.results || [])
    },
    getSites: async function (state, { page = 1, pageSize = 100 } = {}) {
        const scope = buildSiteScope(state)
        if (!scope) return []

        const filters = {
            name: '',
            ...scope,
            page,
            pageSize,
        }

        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.companies.filter}`)
        return response?.data?.results || []
    },
    filterSites: async function (state) {
        const filters = buildSiteScope(state)
        if (!filters) return []

        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.companies.filterV2}`)
        return response?.data?.results || []
    },
    getItinerariesByClient: async function (state, client) {
        const filters = {
            client: client,
            status: 'ACTIVE',
        }

        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.itineraries.filter}`)
        return response?.data?.results || []
    },
    getClientsByAccount: async function (state, account, { page = 1, pageSize = 100 } = {}) {
        const scopedAccount = scopeAccountId(state, account)
        if (!scopedAccount) return []

        const filters = {
            name: '',
            account: scopedAccount,
            status: 'ACTIVE',
            type: 'CLIENT',
            page,
            pageSize,
        }

        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.companies.filter}`)
        return keepOwnClient(state, response?.data?.results || [])
    },
    getSitesByClient: async function (state, client, { page = 1, pageSize = 100 } = {}) {
        const scopedClient = scopeClientId(state, client)
        if (!scopedClient) return []

        const account = hasGlobalScope(state) ? '' : getSessionAccountId(state)

        const filters = {
            name: '',
            client: scopedClient,
            status: 'ACTIVE',
            type: 'SITE',
            page,
            pageSize,
        }

        if (account) filters.account = account

        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.companies.filter}`)
        return response?.data?.results || []
    },
    getSitesByAccount: async function (state, account, { page = 1, pageSize = 100 } = {}) {
        const scopedAccount = scopeAccountId(state, account)
        if (!scopedAccount) return []

        const filters = {
            name: '',
            account: scopedAccount,
            status: 'ACTIVE',
            type: 'SITE',
            page,
            pageSize,
        }

        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.companies.filter}`)
        return response?.data?.results || []
    },
    getEquipmentsBySite: async function (state, body) {
        if (!body) return []

        const filters = {
            account: body?.account,
            client: body?.client,
            site: body?.site,
            status: 'ACTIVE',
            type: 'DEVICE-PHONE',

        }
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, Endpoints.equipments.filter)
        return response?.data?.results || []
    },
    getVigilantsBySite: async function (state, site) {
        const filters = {
            name: '',
            site: site,
            status: 'ACTIVE',
            subtype: 'VIGILANT',
        }

        if (site) {
            const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.systemUsers.customerUser.search}`)

            const result = response?.data?.results
            if (result && result.length > 0) {
                const mappedResult = result.map(item => {
                    return {
                        _id: item._id,
                        firstName: item.firstName,
                        lastName: item.lastName,
                        fullName: `${item.firstName} ${item.lastName}`,
                        status: item.status,
                    }
                })
                return mappedResult
            }
            return []
        }
        return []
    },
    getVigilantsByClient: async function (state, client) {
        const scopedClient = scopeClientId(state, client)

        const filters = {
            name: '',
            client: scopedClient,
            status: 'ACTIVE',
            subtype: 'VIGILANT',
        }

        if (scopedClient) {
            const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.systemUsers.customerUser.search}`)

            const result = response?.data?.results
            if (result && result.length > 0) {
                const mappedResult = result.map(item => {
                    return {
                        _id: item._id,
                        firstName: item.firstName,
                        lastName: item.lastName,
                        fullName: `${item.firstName} ${item.lastName}`,
                        status: item.status,
                    }
                })
                return mappedResult
            }
            return []
        }
        return []
    },
    getVigilantsByAccount: async function (state, account) {
        const scopedAccount = scopeAccountId(state, account)

        const filters = {
            name: '',
            account: scopedAccount,
            status: 'ACTIVE',
            subtype: 'VIGILANT',
        }

        if (scopedAccount) {
            const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.systemUsers.customerUser.search}`)

            const result = response?.data?.results
            if (result && result.length > 0) {
                const mappedResult = result.map(item => {
                    return {
                        _id: item._id,
                        firstName: item.firstName,
                        lastName: item.lastName,
                        fullName: `${item.firstName} ${item.lastName}`,
                        status: item.status,
                    }
                })
                return mappedResult
            }
            return []
        }
        return []
    },
    getSupervisoryPointByClient: async function (state, filters) {
        if (filters) {
            const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.patrolPoints.filter}`)

            const result = response?.data?.results
            if (result && result.length > 0) {
                const mappedResult = result.map(item => {
                    return {
                        ...item,
                        actions: true,
                    }
                })
                return mappedResult
            }
            return []
        }
        return []
    },
    getSupervisorsBySite: async function (state, site) {
        const filters = {
            name: '',
            site: site,
            status: 'ACTIVE',
            subtype: 'SUPERVISOR',
        }

        if (site) {
            const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.systemUsers.customerUser.search}`)

            const result = response?.data?.results
            if (result && result.length > 0) {
                const mappedResult = result.map(item => {
                    return {
                        _id: item._id,
                        firstName: item.firstName,
                        lastName: item.lastName,
                        fullName: `${item.firstName} ${item.lastName}`,
                        status: item.status,
                    }
                })
                return mappedResult
            }
            return []
        }
        return []
    },
    getSupervisorsByClient: async function (state, client) {
        const scopedClient = scopeClientId(state, client)

        const filters = {
            name: '',
            client: scopedClient,
            status: 'ACTIVE',
            subtype: 'SUPERVISOR',
        }

        if (scopedClient) {
            const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.systemUsers.customerUser.search}`)

            const result = response?.data?.results
            if (result && result.length > 0) {
                const mappedResult = result.map(item => {
                    return {
                        _id: item._id,
                        firstName: item.firstName,
                        lastName: item.lastName,
                        fullName: `${item.firstName} ${item.lastName}`,
                        status: item.status,
                    }
                })
                return mappedResult
            }
            return []
        }
        return []
    },
    getVigilantsByGuardGroup: async function (state, guardGroup) {
        const filters = {
            guardGroup: guardGroup,
            status: 'ACTIVE',
        }

        if (guardGroup) {
            const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.guardGroups.filter}`)

            const result = response?.data?.result?.vigilants
            if (result && result?.length > 0) {
                const mappedResult = result.map(item => {
                    return {
                        _id: item._id,
                        firstName: item.firstName,
                        lastName: item.lastName,
                        fullName: `${item.firstName} ${item.lastName}`,
                        status: item.status,
                    }
                })
                return mappedResult
            }
        }

        return []
    },
    externalAnalysisPatrol: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.reports.externalFilter}`)

        return response?.data?.results || []
    },
    getExternalSites: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.companies.externalLocalList}`)

        const results = response?.data?.results
        if (results?.length > 0) {
            const mappedResults = results.map(item => {
                return {
                    ...item,
                    _id: `${item.deptID}-${item.database}`,
                }
            })

            return mappedResults
        }

        return []
    },
    getAppointmentsByDate: async function (state, filters, { rejectOnError = false } = {}) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.appointments.filterV2}`)
        if (rejectOnError) {
            if (response instanceof Error || response?.isAxiosError) throw response
            const data = response?.data
            const hasItems = Array.isArray(data) || Array.isArray(data?.results) || Array.isArray(data?.result)
            if (!response || (response.status !== undefined && response.status !== 200)
                || (data?.status !== undefined && data.status !== 200) || !hasItems) {
                throw new Error('Unable to load calendar appointments')
            }
        }
        return extractResponseItems(response)
    },
    getSchedulesByDate: async function (state, date) {
        const filters = {
            startDate: date,
            isSortByStartDate: true,
        }

        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.schedules.filter}`)

        return response?.data?.results || []
    },
    getGuardGroupsBySite: async function (state, site) {
        if (site) {
            const filters = {
                site: site,
                status: 'ACTIVE',
            }

            const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.guardGroups.filter}`)
            return response?.data?.results || []
        }

        return []
    },
    getPatrolPoints: async function (state, filter) {
        if (filter) {
            const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filter, `${Endpoints.patrolPoints.filter}`)
            return response?.data?.results || []
        }

        return []
    },
    getPatrolPointsBySite: async function (state, site) {
        if (site) {
            const filters = {
                site: site,
                status: 'ACTIVE',
            }

            const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.patrolPoints.filter}`)
            return response?.data?.results || []
        }

        return []
    },
    getSupervisionCheckPointsByClient: async function (state, client) {
        if (client) {
            const filters = {
                client: client,
                type: 'SUPERVISION',
                status: 'ACTIVE',
            }

            const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.patrolPoints.filter}`)
            return response?.data?.results || []
        }

        return []
    },

    getPatrolActions: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.patrolActions.filter}`)

        if (response instanceof Error || response?.isAxiosError) {
            throw response
        }

        return response?.data?.results || []
    },
    getPatrolActionsPaginated: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.patrolActions.filter}`)

        if (response instanceof Error || response?.isAxiosError) {
            throw response
        }

        return {
            results: response?.data?.results || [],
            totalCount: response?.data?.totalCount ?? null,
            page: response?.data?.page ?? null,
            limit: response?.data?.limit ?? null,
        }
    },
    getExternalCompanies: async function (state) {
        const response = await Request.do(state, 'GET', Request.getDefaultHeader(state), {}, `${Endpoints.companies.externalList}`)

        const results = response?.data?.results

        if (results?.length > 0) {
            const mappedResults = results.map(item => {
                return {
                    ...item,
                    _id: `${item.deptID}-${item.database}`,
                }
            })

            return mappedResults
        }

        return []
    },
    getGatewayExternalCompanies: async function (state) {
        const response = await Request.do(state, 'GET', Request.getDefaultHeader(state), {}, `${Endpoints.companies.gatewayExternalList}`)

        const results = response?.data?.results

        if (results?.length > 0) {
            const mappedResults = results.map(item => {
                return {
                    ...item,
                    _id: `${item.deptID}-${item.database}`,
                }
            })

            return mappedResults
        }

        return []
    },
    getPatrolActionById: async function (state, id) {
        const response = await Request.do(state, 'GET', Request.getDefaultHeader(state), {}, `${Endpoints.patrolActions.patrolAction}/${id}`)
        return response?.data?.result || {}
    },
    getAttendancesOptionsTypes: async function (state) {
        const response = await Request.do(state, 'GET', Request.getDefaultHeader(state), {}, `${Endpoints.helpers.attendancesTypes}`)
        return response?.data?.results || []
    },
    getReportNamesTypes: async function (state) {
        const response = await Request.do(state, 'GET', Request.getDefaultHeader(state), {}, `${Endpoints.helpers.reportNames}`)
        return response?.data?.results || []
    },
    getIntegrationReportNamesTypes: async function (state) {
        const response = await Request.do(state, 'GET', Request.getDefaultHeader(state), {}, `${Endpoints.helpers.reportIntegrationNames}`)
        return response?.data?.results || []
    },
    getEventAttendances: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.events.attendances.filter}`)
        return response?.data?.results || []
    },
    getScheduleById: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.schedules.filter}`)
        return response?.data?.results[0] || []
    },
    getVigilantScheduleById: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.schedules.v2.filter}`)
        const items = extractResponseItems(response)
        return items[0] || null
    },
    cancelAppointmentSeries: async function (state, filters) {
        try {
            const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.appointments.cancelSeries}`)
            if (response.status === 200) {
                return true
            }

            return null
        } catch {
            return null
        }
    },
    updateScheduleSeries: async function (state, body) {
        try {
            const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), body, `${Endpoints.schedules.update}`)
            if (response.status === 200) {
                return true
            }

            return null
        } catch {
            return null
        }
    },
    updateAppointmentOccurence: async function (state, body) {
        try {
            const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), body, `${Endpoints.appointments.update}`)
            if (response.status === 200) {
                return true
            }

            return null
        } catch {
            return null
        }
    },
    cancelAppointmentOccurrence: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.appointments.cancelOccurrence}`)
        return response?.data?.deleteAppointment || null
    },
    getEventsByDate: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.events.filter}`)
        return response?.data?.results || []
    },
    getEventsPage: async function (state, filters) {
        return await getTimelinePage(state, filters, `${Endpoints.events.filter}`)
    },
    getAlertOccurrences: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.schedules.occurences.filter}`)
        const results = response?.data?.results || []
        // Normalize paginated responses: some endpoints return [{ paginatedResults: [...], totalCount: [...] }]
        if (Array.isArray(results) && results.length > 0 && results[0].paginatedResults) {
            return results[0].paginatedResults || []
        }
        return results
    },
    getAlertOccurrencesPage: async function (state, filters) {
        return await getTimelinePage(state, filters, `${Endpoints.schedules.occurences.filter}`)
    },
    getTimeEntries: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.schedules.timeentries.filter}`)
        const results = response?.data?.results || []
        // Normalize paginated responses: some endpoints return [{ paginatedResults: [...], totalCount: [...] }]
        if (Array.isArray(results) && results.length > 0 && results[0].paginatedResults) {
            return results[0].paginatedResults || []
        }
        return results
    },
    getTimeEntriesPage: async function (state, filters) {
        return await getTimelinePage(state, filters, `${Endpoints.schedules.timeentries.filter}`)
    },
    getPatrolsChart: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.charts.patrols}`)
        return response?.data?.result || {}
    },
    getEventsAttendance: async function (state, filters) {
        filters.types = EVENT_CHART_TYPES
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.charts.eventsAttendance}`)
        return response?.data?.result || {}
    },
    getAvaregeTimeAttendanceEvent: async function (state, filters) {
        filters.types = EVENT_CHART_TYPES
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.charts.avaregeTimeAttendanceEvent}`)
        return response?.data?.result || {}
    },
    getEventsByType: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.charts.eventsByType}`)
        return response?.data?.result || {}
    },
    getFreePatrols: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.patrols.freePatrols}`)
        return response?.data?.results || []
    },
    getFreePatrolsPage: async function (state, filters) {
        return await getTimelinePage(state, filters, `${Endpoints.patrols.freePatrols}`)
    },
    getFormattedEventsByDate: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.events.filter}`)
        const results = response?.data?.results
        if (results?.length > 0) {
            const mappedResults = results.map(item => {
                return {
                    _id: item._id,
                    event: item.name,
                    vigilant: item.vigilant.fullName,
                    startDate: moment.tz(item.startDate, Common.getAccountTimezone()).format('YYYY-MM-DD HH:mm:ss'),
                    endDate: moment.tz(item.endDate, Common.getAccountTimezone()).format('YYYY-MM-DD HH:mm:ss'),
                    account: item.account.name,
                    client: item.client.name,
                    site: item.site.name,
                }
            })
            return mappedResults
        }
        return []
    },
    emailAlreadyExists: async function (state, email) {
        const response = await Request.do(state, 'GET', Request.getDefaultHeader(state), {}, `${Endpoints.systemUsers.checkEmailExist}${email}`)
        return response?.data?.result || null
    },
    usernameAlreadyExists: async function (state, username) {
        const response = await Request.do(state, 'GET', Request.getDefaultHeader(state), {}, `${Endpoints.systemUsers.checkUsernameExist}${username}`)
        return response?.data?.result || null
    },
    getPatrolActionsByPatrol: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.patrolActions.filter}`)
        return response?.data?.results || []
    },
    getPatrolActionsByEvent: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.patrolActions.filter}`)
        return response?.data?.results || []
    },
    createAttendance: async function (state, attendance) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), attendance, `${Endpoints.events.attendances.attendance}`)
        return response?.data?.result || null
    },
    patrolPointsPerformance: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.charts.patrolPointsPerformance}`)
        return response?.data?.result || []
    },
    localsPerformance: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.charts.localsPerformance}`)
        return response?.data?.result || []
    },
    guardsPerformance: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.charts.guardsPerformance}`)
        return response?.data?.result || []
    },
    eventsPerformance: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.charts.eventsPerformance}`)
        return response?.data?.result || {}
    },
    eventsSummary: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.charts.analisys.events}`)
        return response?.data?.result || {}
    },
    checkpointsSummary: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.charts.analisys.checkpoints}`)
        return response?.data?.result || {}
    },
    eventsPerDay: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.charts.analisys.eventsPerDay}`)
        return response?.data?.result || {}
    },
    eventsSubstatusPerDay: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.charts.analisys.eventsSubstatusPerDay}`)
        return response?.data?.result || {}
    },
    checkpointsPerDay: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.charts.analisys.checkpointsPerDay}`)
        return response?.data?.result || {}
    },
    eventsPerformanceSubstatus: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.charts.eventsPerformanceSubstatus}`)
        return response?.data?.result || {}
    },
    getEquipmentBrands: async function (state) {
        const response = await Request.do(state, 'GET', Request.getDefaultHeader(state), {}, `${Endpoints.helpers.equipmentBrands}`)
        return response?.data?.results || []
    },
    getEquipmentTypes: async function (state) {
        const response = await Request.do(state, 'GET', Request.getDefaultHeader(state), {}, `${Endpoints.helpers.equipmentTypes}`)
        return response?.data?.results || []
    },
    getTimezones: async function (state) {
        const response = await Request.do(state, 'GET', Request.getDefaultHeader(state), {}, `${Endpoints.helpers.timezones}`)
        const results = response?.data?.results || []

        const timezonesFromApi = results
            .map(item => item?._id)
            .filter(Boolean)

        const sourceTimezones = [...moment.tz.names(), ...timezonesFromApi, 'UTC']
        const mappedTimezones = sourceTimezones
            .map(timezone => Common.buildTimezoneOption(timezone))
            .filter(Boolean)
        const uniqueTimezones = [...new Map(mappedTimezones.map(item => [item._id, item])).values()]
        const sortedTimezones = uniqueTimezones.sort((a, b) => {
            if (a.offsetInMinutes !== b.offsetInMinutes) {
                return a.offsetInMinutes - b.offsetInMinutes
            }

            return a.name.localeCompare(b.name)
        })

        if (sortedTimezones.length > 0) {
            return sortedTimezones.map(item => {
                return {
                    _id: item._id,
                    name: item.name,
                }
            })
        }

        return [{ _id: 'UTC', name: '(GMT+00:00) UTC' }]
    },
    attendanceEvent: async function (state, patrolActionId, attendance, siteGroup) {
        const body = {
            patrolActionId: patrolActionId,
            attendance: attendance,
            siteGroup: siteGroup,
        }

        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), body, `${Endpoints.patrolActions.attendanceEvent}`)
        return response?.data?.result || null
    },
    getSiteGroupsByAccount: async function (state, account) {
        const scopedAccount = scopeAccountId(state, account)
        if (!scopedAccount) return []

        const filters = {
            account: scopedAccount,
            status: 'ACTIVE',
        }

        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.siteGroups.filter}`)
        return response?.data?.results || []
    },
    getSiteGroupsByClient: async function (state, client) {
        const filters = {
            client: client,
            status: 'ACTIVE',
        }
        if (!applySessionScope(state, filters)) return []

        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.siteGroups.filter}`)
        return response?.data?.results || []
    },
    getClientGroupsByAccount: async function (state, account) {
        const filters = {
            account: account,
            status: 'ACTIVE',
        }

        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.clientGroups.filter}`)
        return response?.data?.results || []
    },
    searchPatrols: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.reports.filter}`)
        const results = response?.data?.results

        if (results?.length > 0) {
            const flattenedItems = results.reduce((acc, item) => {
                const items = item.schedules.reduce((acc, schedule) => {
                    return [
                        ...acc,
                        ...schedule?.items.reduce((acc, i) => {
                            return [
                                ...acc,
                                ...i?.actions.map(action => ({
                                    patrolPoint: action.patrolPoint?.name,
                                    event: schedule.name,
                                    vigilant: i.vigilant,
                                    scannedDate: action.date ? moment.tz(action.date, Common.getAccountTimezone()).format('DD/MM/YYYY HH:mm:ss') : '',
                                    account: item.name,
                                    client: i.client,
                                    site: i.site,
                                    startDate: moment.tz(i.startDate, Common.getAccountTimezone()).format('DD/MM/YYYY HH:mm:ss'),
                                    endDate: moment.tz(i.endDate, Common.getAccountTimezone()).format('DD/MM/YYYY HH:mm:ss'),
                                    frequency: schedule.frequency,
                                })),
                            ]
                        }, []),
                    ]
                }, [])
                return [...acc, ...items]
            }, [])
            return {
                tableItems: flattenedItems,
                reportItems: results,
            }
        }

        return []
    },

    searchMissedPatrols: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.reports.filter}`)
        const results = response?.data?.results

        if (results?.length > 0) {
            const flattenedItems = results.reduce((acc, item) => {
                const schedules = item.schedules.reduce((acc, schedule) => {
                    const items = schedule?.items.reduce((acc, i) => {
                        const points = i.points.map(point => ({
                            patrolPoint: point,
                            event: schedule.name,
                            vigilant: schedule.vigilant,
                            scannedDate: '', // Nenhuma data de escaneamento fornecida na nova estrutura
                            account: item.name,
                            client: i.client,
                            site: i.site,
                            startDate: moment.tz(i.startDate, Common.getAccountTimezone()).format('DD/MM/YYYY HH:mm:ss'),
                            endDate: moment.tz(i.endDate, Common.getAccountTimezone()).format('DD/MM/YYYY HH:mm:ss'),
                            frequency: schedule.frequency,
                        }))
                        return [...acc, ...points]
                    }, [])
                    return [...acc, ...items]
                }, [])
                return [...acc, ...schedules]
            }, [])

            return {
                tableItems: flattenedItems,
                reportItems: results,
            }
        }

        return []
    },

    mergeArrays: async function (completed, incompleted, notVisited) {
        // Inicializando variáveis para garantir que sempre tenhamos um objeto válido
        const defaultObj = { name: 'Default Name', address: 'Default Address', schedules: [] }

        // Verificar se há pelo menos um item em cada array antes de tentar acessar a posição 0
        const safeGetFirst = array => (array && array.length > 0 ? array[0] : defaultObj)

        const firstCompleted = safeGetFirst(completed)
        const firstIncompleted = safeGetFirst(incompleted)
        const firstNotVisited = safeGetFirst(notVisited)

        // Construindo o array com dados unificados
        const array = [
            {
                name: firstCompleted.name || firstIncompleted.name || firstNotVisited.name,
                address: firstCompleted.address || firstIncompleted.address || firstNotVisited.address,
                schedules: [...(firstCompleted.schedules || []), ...(firstIncompleted.schedules || []), ...(firstNotVisited.schedules || [])],
            },
        ]
        return array
    },
    async searchAllPatrols(state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.reports.filter}`)
        const results = response?.data?.results || []

        async function processResults(resultArray) {
            const processedResults = []

            for (const item of resultArray) {
                const startDateFormatted = moment.tz(item.startDate, Common.getAccountTimezone()).format('DD/MM/YYYY HH:mm:ss')
                const endDateFormatted = moment.tz(item.endDate, Common.getAccountTimezone()).format('DD/MM/YYYY HH:mm:ss')

                const readPoints = (item.patrolPointsChecked || []).map(p => p.name).join(', ')
                const unreadPoints = (item.patrolPointsUnchecked || []).map(p => p.name).join(', ')

                processedResults.push({
                    name: item.name || 'N/A',
                    status: item.status || 'N/A',
                    substatus: item.substatus || 'N/A',
                    startDate: startDateFormatted,
                    endDate: endDateFormatted,
                    account: item.account?.name || 'N/A',
                    client: item.client?.name || 'N/A',
                    site: item.site?.name || 'N/A',
                    checkedPoints: readPoints,
                    uncheckedPoints: unreadPoints,
                })
            }

            // get all the patrol points by status
            const completed = processedResults.filter(item => item.substatus === 'COMPLETE')
            const incompleted = processedResults.filter(item => item.substatus === 'INCOMPLETE')
            const notVisited = processedResults.filter(item => item.substatus === 'NOT_STARTED')

            const reportItems = {
                completed: completed,
                incompleted: incompleted,
                notVisited: notVisited,
            }

            return {
                tableItems: processedResults,
                reportItems: reportItems,
            }
        }

        return await processResults(results)
    },

        scanLocalHistory: async function (state, filters) {
            // Calls generic reports filter endpoint which should dispatch to scan local history report
            const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.reports.filter}`)
            const results = response?.data?.results || []

            if (results && results.length > 0) {
                // Expected structure from backend: array of accounts with nested clients -> sites -> dates -> readings
                const flattened = []

                results.forEach(account => {
                    // backend may return account name in different keys: accountName, name, or nested account.name
                    const accountName = account.accountName || account.name || account.account || (account.account && account.account.name) || ''
                    const clients = account.clients || account.client || []

                    clients.forEach(client => {
                        // client may have clientName, name, or nested client.name
                        const clientName = client.clientName || client.name || client.client || (client.client && client.client.name) || ''
                        const sites = client.sites || client.site || []

                        sites.forEach(site => {
                            // site may have siteName, name, or nested site.name
                            const siteName = site.siteName || site.name || site.site || (site.site && site.site.name) || ''

                            // New backend structure: site.patrolPoints -> each patrolPoint has dates[] -> each date has readings[]
                            const patrolPoints = site.patrolPoints || []

                            if (Array.isArray(patrolPoints) && patrolPoints.length > 0) {
                                patrolPoints.forEach(pp => {
                                    const pointName = pp.patrolPointName || pp.name || pp.patrolPoint || ''
                                    const dates = pp.dates || []

                                    dates.forEach(dateObj => {
                                        const date = dateObj.date || ''
                                        const readings = dateObj.readings || []

                                        // readings are expected already ordered by time asc by the backend
                                        const scans = readings.map(r => r.time || '').filter(t => t)

                                        flattened.push({
                                            account: accountName,
                                            client: clientName,
                                            site: siteName,
                                            point: pointName,
                                            date: moment.tz(date, Common.getAccountTimezone()).format('DD/MM/YYYY'),
                                            scans: scans,
                                        })
                                    })
                                })
                            } else {
                                // Fallback for older backend shape: site.dates -> each date has readings[] with patrolPointName inside
                                const dates = site.dates || []

                                dates.forEach(dateObj => {
                                    const date = dateObj.date || ''
                                    const readings = dateObj.readings || []

                                    // Group readings by patrolPointName (point)
                                    const byPoint = {}

                                    readings.forEach(r => {
                                        const point = r.patrolPointName || r.point || ''
                                        const time = r.time || ''
                                        if (!byPoint[point]) byPoint[point] = []
                                        if (time) byPoint[point].push(time)
                                    })

                                    Object.keys(byPoint).forEach(pointName => {
                                        flattened.push({
                                            account: accountName,
                                            client: clientName,
                                            site: siteName,
                                            point: pointName,
                                            date: moment.tz(date, Common.getAccountTimezone()).format('DD/MM/YYYY'),
                                            scans: byPoint[pointName],
                                        })
                                    })
                                })
                            }
                        })
                    })
                })

                // garantir que o flattened esteja ordenado por nome do ponto asc e data descrescente
                flattened.sort((a, b) => {
                    const dateA = moment(a.date, 'DD/MM/YYYY')
                    const dateB = moment(b.date, 'DD/MM/YYYY')
                    if (dateA.isSame(dateB)) {
                        return a.point.localeCompare(b.point)
                    }
                    return dateB - dateA
                })
                

                return {
                    tableItems: flattened,
                    reportItems: results,
                }
            }

            return []
        },
    searchAlonePatrols: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.reports.filter}`)
        const results = response?.data?.results

        if (results?.length > 0) {
            const flattenedItems = results.reduce((acc, item) => {
                const items = item.patrols.reduce((acc, patrol) => {
                    const patrolItems = patrol.items.map(i => ({
                        _id: i._id,
                        patrolPoint: i.name,
                        vigilant: i.vigilant,
                        scannedDate: i.date,
                        startDate: patrol.startDate,
                        endDate: patrol.endDate,
                        account: i.account,
                        client: i.client,
                        site: i.site,
                        status: i.status,
                        latitude: i.geolocation.latitude,
                        longitude: i.geolocation.longitude,
                        deviceId: i.deviceInfo.deviceId,
                    }))

                    return [...acc, ...patrolItems]
                }, [])
                return [...acc, ...items]
            }, [])

            return {
                tableItems: flattenedItems,
                reportItems: results,
            }
        }

        return []
    },
    incidents: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.reports.filter}`)
        const results = response?.data?.results
        if (results?.length > 0) {
            const flattenedItems = results.reduce((acc, account) => {
                const items = account.clients.reduce((acc, client) => {
                    return [
                        ...acc,
                        ...client?.sites.reduce((acc, site) => {
                            return [
                                ...acc,
                                ...site?.items.map(item => ({
                                    date: item.date ? moment.tz(item.date, Common.getAccountTimezone()).format('DD/MM/YYYY HH:mm:ss') : '',
                                    vigilant: item.vigilant,
                                    geolocation: item.geolocation,
                                    event: item?.event ? item.event.name : '',
                                    incidents: item.incidents,
                                    notes: item.notes,
                                    medias: {
                                        soundURL: item.soundURL,
                                        photoURL: item.photoURL,
                                        signatureURL: item.signatureURL,
                                    },
                                    account: account.account,
                                    client: client.name,
                                    site: site.name,
                                    attendance: item.attendance,
                                    deviceInfo: item.deviceInfo,
                                    status: item.status,
                                })),
                            ]
                        }, []),
                    ]
                }, [])
                return [...acc, ...items]
            }, [])

            return {
                tableItems: flattenedItems,
                reportItems: results,
            }
        }
    },
    missedCall: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.reports.filter}`)
        const results = response?.data?.results

        if (results?.length > 0) {
            const flattenedItems = results.reduce((acc, account) => {
                const items = account.clients.reduce((acc, client) => {
                    return [
                        ...acc,
                        ...client?.sites.reduce((acc, site) => {
                            return [
                                ...acc,
                                ...site?.items.map(item => ({
                                    date: item.date ? moment.tz(item.date, Common.getAccountTimezone()).format('DD/MM/YYYY HH:mm:ss') : '',
                                    vigilant: item.vigilant,
                                    type: item?.type ? state.$t(item.type) : '',
                                    event: item?.event ? item.event.name : '',
                                    incidents: item.incidents,
                                    notes: item.notes,
                                    account: account.account,
                                    client: client.name,
                                    site: site.name,
                                    deviceInfo: item.deviceInfo,
                                    status: item.status,
                                })),
                            ]
                        }, []),
                    ]
                }, [])
                return [...acc, ...items]
            }, [])

            return {
                tableItems: flattenedItems || [],
                reportItems: results || [],
            }
        }
    },
    getPatrolPointsByClient: async function (state, client) {
        const filters = {
            client: client,
            status: 'ACTIVE',
            type: 'SUPERVISION',
        }

        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.patrolPoints.filter}`)
        return response?.data?.results || []
    },
    SupervisorPatrol: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.reports.filter}`)
        const results = response?.data?.results

        if (results?.length > 0) {
            const formattedResults = results.map(result => {
                return {
                    account: result.account?.name,
                    accountAddress: result.account?.address,
                    client: result.client?.name,
                    site: result.site?.name,
                    vigilant: result?.vigilant?.fullName,
                    patrolPoints: result?.patrolPoints,
                }
            })

            const allPatrolPoints = [].concat(...formattedResults.map(result => result.patrolPoints))

            allPatrolPoints.sort((a, b) => {
                if (a.read === false) {
                    return -1
                }
                if (b.read === false) {
                    return 1
                }
                return 0
            })

            const finalResult = {
                account: formattedResults[0].account,
                accountAddress: formattedResults[0].accountAddress,
                client: formattedResults[0].client,
                site: formattedResults[0].site,
                vigilant: formattedResults[0].vigilant,
                patrolPoints: allPatrolPoints,
            }

            return finalResult
        }

        return null // Ou algum outro valor padrão em caso de não haver resultados
    },

    sosAlerts: async function (state, filters) {
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.reports.filter}`)
        const results = response?.data?.results

        if (results?.length > 0) {
            const flattenedItems = results.reduce((acc, item) => {
                const items = item.items.reduce((acc, i) => {
                    return [
                        ...acc,
                        {
                            date: i.date ? moment.tz(i.date, Common.getAccountTimezone()).format('DD/MM/YYYY HH:mm:ss') : '',
                            vigilant: i.vigilant,
                            geolocation: i.geolocation,
                            event: i.event,
                            attendance: i.attendance,
                            deviceInfo: i.deviceInfo,
                            account: i.account,
                            client: i.client,
                            site: i.site,
                        },
                    ]
                }, [])
                return [...acc, ...items]
            }, [])

            return {
                tableItems: flattenedItems,
                reportItems: results,
            }
        }
        return []
    },
    dataHistory: async function (state, filters) {
        // Calls generic reports filter endpoint which should dispatch to dataHistory on backend
        const response = await Request.do(state, 'POST', Request.getDefaultHeader(state), filters, `${Endpoints.reports.filter}`)
        const results = response?.data?.results || []

        if (results && results.length > 0) {
            // Expecting backend to return flat list of rows with fields: plannedDate, readingTime, accountName, siteName, patrolPointName, status (key)
            const flattened = results.map(r => {
                return {
                    plannedDate: r.plannedDate,
                    readingTime: r.readingTime || '',
                    account: r.accountName || r.account || (r.account && r.account.name) || '',
                    client: r.clientName || r.client || (r.client && r.client.name) || '',
                    site: r.siteName || r.site || (r.site && r.site.name) || '',
                    point: r.point,
                    status: r.status || '',
                    visited: typeof r.visited !== 'undefined' ? r.visited : null,
                }
            })

            return {
                tableItems: flattened,
                reportItems: results,
            }
        }

        return []
    },
    filterReports: async function (state, filters) {
        switch (filters?.report) {
            case REPORT_PATROL_TYPE_LIST.PATROL_POINTS_COMPLETED:
                return this.searchPatrols(state, filters)
            case REPORT_PATROL_TYPE_LIST.PATROL_POINTS_INCOMPLETED:
                return this.searchPatrols(state, filters)
            case REPORT_PATROL_TYPE_LIST.PATROL_POINTS_NOT_VISITED:
                return this.searchMissedPatrols(state, filters)
            case REPORT_PATROL_TYPE_LIST.ALL_PATROLS:
                return this.searchAllPatrols(state, filters)
            case REPORT_PATROL_TYPE_LIST.ALONE_PATROLS:
                return this.searchAlonePatrols(state, filters)

            default:
                break
        }
    },
}
</script>
